export const RESERVED_TEMPLATE_SLUGS: ReadonlySet<string> = new Set<string>([
  "new",
  "edit",
  "settings",
  "api",
  "templates",
  "compose",
  "logs",
  "brand",
  "dashboard",
  "app",
  "admin",
  "sign-in",
  "sign-up",
  "me",
  "template",
]);

export function slugify(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80) || "template"
  );
}

export function isReservedSlug(slug: string): boolean {
  return RESERVED_TEMPLATE_SLUGS.has(slug);
}

export function isAutoSuffixedSlug(name: string, slug: string): boolean {
  return slug !== slugify(name);
}

export type SlugAdjustmentReason = "reserved" | "collision";

export interface SlugAdjustmentWarning {
  reason: SlugAdjustmentReason;
  baseSlug: string;
}

/**
 * Predicts whether saving a template with the given `name` would cause the
 * server to auto-suffix the URL slug, either because the resulting base slug
 * is reserved or because it collides with an existing slug owned by this user.
 *
 * `existingSlugs` should be the slugs the current user already owns.
 * `ignoreSlug`, when provided, is excluded from the collision check (used by
 * a rename form so a template doesn't conflict with itself).
 */
export function predictSlugAdjustment(
  name: string,
  existingSlugs: Iterable<string>,
  ignoreSlug?: string,
): SlugAdjustmentWarning | null {
  const trimmed = name.trim();
  if (!trimmed) return null;
  const baseSlug = slugify(trimmed);
  if (isReservedSlug(baseSlug)) {
    return { reason: "reserved", baseSlug };
  }
  for (const slug of existingSlugs) {
    if (slug === ignoreSlug) continue;
    if (slug === baseSlug) {
      return { reason: "collision", baseSlug };
    }
  }
  return null;
}
